
import Vue from "vue";
import { Component, Inject, Prop } from "vue-property-decorator";
import draggable from "vuedraggable";
import { ElementStudioContext } from "@/components/ElementStudio/ElementStudioTabsAndPanels.vue";
import { configStore, uiStore } from "@/store/store-accessor";
import Accordion from "@/components/UI/Accordion.vue";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";
import Repeater from "@/components/Reusable/CustomInputs/Repeater/Repeater.vue";
import defensiveCopy from "@/util/defensive-copy";
import { isNestingRuleAllowedInside } from "@/lib/tree/nesting";
import { DefaultChildElement } from "@/types/elements";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import ElementStudioDefaultProperties from "@/components/ElementStudio/ElementStudioDefaultProperties.vue";
import NiceButton from "@/components/UI/NiceButton.vue";
import { TreeNode } from "@/types/tree";

@Component({
  components: {
    NiceButton,
    ElementStudioDefaultProperties,
    TextInput,
    Repeater,
    Dropdown,
    Accordion,
    draggable,
  },
})
export default class ElementStudioDefaultElements extends Vue {
  @Prop()
  value!: DefaultChildElement[];

  @Prop()
  parentElementSlug!: string;

  @Prop({ default: true })
  allowImport!: boolean;

  defaultValue = {};

  repeaterOptions = {
    options: {
      repeaterOptions: {
        defaultTitle: "Default Element",
        titleTemplate: "{slug}",
        buttonName: "Add new default element",
      },
    },
  };

  @Inject("context") readonly context!: ElementStudioContext;

  beforeCreate() {
    // @ts-ignore
    this.$options.components.ElementStudioDefaultElements =
      require("./ElementStudioDefaultElements.vue").default;
  }

  updateDefaultElements(newValue: DefaultChildElement[]) {
    this.$emit("input", newValue);
  }

  canHaveChildren(slug: string) {
    if (!slug) return false;

    const element = configStore.element(slug);
    const type = element.nestingRule.type;
    return !type.startsWith("final");
  }

  get canImport() {
    if (!uiStore.activeElement) return false;
    return uiStore.activeElement.data.type === this.context.element().slug;
  }

  importChildren() {
    if (!uiStore.activeElement) return;

    const transform = (element: TreeNode): DefaultChildElement => {
      return {
        slug: element.data.type,
        defaultProperties: element.data.properties,
        children: element.children.map(transform),
      };
    };

    const activeElement = defensiveCopy(uiStore.activeElement);

    const newChildren = activeElement.children.map(transform);

    this.updateDefaultElements(newChildren);
  }

  get elementsAvailableForThisElementNestingType() {
    return Object.keys(configStore.elements).filter((slug) => {
      const parentElement = configStore.element(this.parentElementSlug);
      const currentElementNestingRule = configStore.element(slug).nestingRule;

      return isNestingRuleAllowedInside(
        currentElementNestingRule,
        parentElement.nestingRule,
        parentElement.slug
      );
    });
  }

  updatePath(
    value: any,
    repeaterIndex: number,
    path: keyof DefaultChildElement
  ) {
    const newValue = defensiveCopy(this.value) || {};
    newValue[repeaterIndex][path] = value;

    this.$emit("input", newValue);
  }
}
